import * as React from "react";
import { Link, LinkProps } from "react-router-dom";
import { LocationDescriptor } from "history";
import ShavedText from "common/components/shavedText";
import RoundImage from "common/components/roundImage";
import { IconWrapper } from "common/icons";
import PACKS, { IIconPack } from "common/icons/packs";
import { FormattedNumber } from "react-intl";
import MediaThumbnail from "../compactList/components/mediaThumbnail";
import {
  IUseTrackTicketOption,
  useTrackTicket,
} from "@vingle/wtf-sdk/helpers/trackTicket";
import { VingleURL } from "@vingle/wtf-sdk/url";
import { useIntl } from "common/intl/helpers";

const styles = require("./compactCard.scss");

type CompactCardWrapperElement = HTMLAnchorElement | HTMLDivElement;

interface IProps {
  link: LocationDescriptor;
  title?: string | null;
  content?: string;
  description?: string;
  author?: Vingle.User.ISimplifiedUser;
  engage?: {
    commentCount: number;
    clipCount: number;
    isClipped: boolean;
    likeCount: number;
    isLiked: boolean;
  };
  media?: {
    src: string | null;
    type: Vingle.MediaType;
  };
  onClick?: React.MouseEventHandler<CompactCardWrapperElement>;
  trackTicket?: IUseTrackTicketOption;
}

const TITLE_LINE_HEIGHT = 28;
const CONTENT_LINE_HEIGHT = 19.5;

interface IEngageUnitProps {
  icon: IIconPack;
  count: number;
}

const EngageUnit = ({ icon, count }: IEngageUnitProps) => {
  if (isNaN(count)) return null;

  return (
    <div className={styles.engageUnit}>
      <IconWrapper icon={icon} className={styles.engageUnitIcon} />
      <span>
        <FormattedNumber value={count} />
      </span>
    </div>
  );
};

type CompactCardHTMLProps = LinkProps | (React.HTMLProps<HTMLDivElement>);
const LinkOrDiv = React.forwardRef<
  CompactCardWrapperElement,
  {
    to?: LocationDescriptor;
    children: React.ReactNode;
  } & CompactCardHTMLProps
>(({ to, children, ...rest }, ref) => {
  if (to) {
    return (
      <Link
        to={to}
        innerRef={ref as React.Ref<HTMLAnchorElement>}
        {...rest as LinkProps}
      >
        {children}
      </Link>
    );
  } else {
    return (
      <div
        {...rest as React.HTMLProps<HTMLDivElement>}
        ref={ref as React.Ref<HTMLDivElement>}
      >
        {children}
      </div>
    );
  }
});

const CompactCard = React.memo(
  React.forwardRef<CompactCardWrapperElement, IProps>((props, ref) => {
    const {
      link,
      onClick,
      trackTicket,
      title,
      content,
      description,
      media,
      author,
      engage,
    } = props;
    const { impressionRef, handleRead } = useTrackTicket(trackTicket);
    const { handleRead: handleAuthorRead } = useTrackTicket(
      author && {
        contentType: "user",
        contentId: author.id,
        referralUrl: trackTicket && trackTicket.referralUrl,
      },
    );
    const intl = useIntl();
    const handleClick = React.useCallback(
      (event: React.MouseEvent<CompactCardWrapperElement>) => {
        if (onClick) {
          onClick(event);
        }
        handleRead();
      },
      [handleRead, onClick],
    );
    const handleRef = React.useCallback(
      (element: CompactCardWrapperElement | null) => {
        if (impressionRef) {
          impressionRef(element);
        }
        if (typeof ref === "function") {
          ref(element);
        }
      },
      [impressionRef],
    );
    const hasUserArea = Boolean(author);
    const linkWrapper = !hasUserArea ? link : undefined;
    const linkSection = hasUserArea ? link : undefined;
    return (
      <div className={styles.cardWrapper}>
        <LinkOrDiv
          to={linkWrapper}
          ref={handleRef}
          className={styles.cardContainer}
          onClick={handleClick}
        >
          {media ? (
            <LinkOrDiv to={linkSection} className={styles.rightWrapper}>
              <div className={styles.imageWrapper}>
                <MediaThumbnail
                  type={media.type}
                  url={media.src}
                  wrapperStyle={{ width: "100%", height: "100%" }}
                />
              </div>
            </LinkOrDiv>
          ) : null}
          <div className={styles.leftWrapper}>
            <LinkOrDiv to={linkSection} className={styles.contentWrapper}>
              <ShavedText
                value={
                  title ? title.trim() : intl.formatMessage({ id: "no_title" })
                }
                className={styles.title}
                shaveHeight={TITLE_LINE_HEIGHT * (content ? 1 : 2)}
              />
              {content ? (
                <ShavedText
                  value={content}
                  className={styles.content}
                  shaveHeight={CONTENT_LINE_HEIGHT * (title ? 1 : 2)}
                />
              ) : null}
            </LinkOrDiv>
            {
              <footer className={styles.footer}>
                {author ? (
                  <Link
                    to={new VingleURL.UserShow({
                      username: author.username,
                    }).toString()}
                    className={styles.user}
                    onClick={handleAuthorRead}
                  >
                    {author.profile_image_urls ? (
                      <RoundImage
                        imageSrc={author.profile_image_urls}
                        width={20}
                        height={20}
                        wrapperClassName={styles.userImage}
                      />
                    ) : null}
                    <span className={styles.username}>{author.username}</span>
                  </Link>
                ) : null}
                {description && (
                  <div className={styles.description}>{description}</div>
                )}
                {engage ? (
                  <>
                    {engage.likeCount ? (
                      <EngageUnit
                        icon={
                          engage.isLiked
                            ? PACKS.ENGAGE.SMALL_LIKE_ACTIVE
                            : PACKS.ENGAGE.SMALL_LIKE_GREY_NORMAL
                        }
                        count={engage.likeCount}
                      />
                    ) : null}
                    {engage.clipCount ? (
                      <EngageUnit
                        icon={
                          engage.isClipped
                            ? PACKS.ENGAGE.SMALL_CLIP_ACTIVE
                            : PACKS.ENGAGE.SMALL_CLIP_GREY_NORMAL
                        }
                        count={engage.clipCount}
                      />
                    ) : null}
                    {engage.commentCount ? (
                      <EngageUnit
                        icon={PACKS.ENGAGE.SMALL_COMMENT_GREY_NORMAL}
                        count={engage.commentCount}
                      />
                    ) : null}
                  </>
                ) : null}
              </footer>
            }
          </div>
        </LinkOrDiv>
      </div>
    );
  }),
);
CompactCard.displayName = "CompactCard";
export default CompactCard;
